/**
 * Dynamix compression type 2 — LZW, 9→12 bit codes, LSB-first bit packing
 * (docs/formats/compression.md, VERIFIED against all 200 packed chunks in
 * the shipped volumes).
 *
 * The dictionary-reset rule is the trap: on code 0x100, keep reading (and
 * discarding) codewords until the count read since the last reset is a
 * multiple of 8, THEN clear the dictionary. Getting this wrong still
 * produces output of exactly the declared length — only rendering the
 * result exposes the corruption.
 */

const LZW_RESET_CODE = 0x100;
const LZW_FIRST_CODE = 0x101;
const LZW_MAX_TABLE_SIZE = 4096;
const LZW_MAX_WIDTH = 12;
const LZW_MIN_WIDTH = 9;

/** LSB-first bit reader over a byte array: bits within a byte read low-to-high, bytes consumed in order. */
class LsbBitReader {
  private readonly data: Uint8Array;
  private bitPos = 0;

  constructor(data: Uint8Array) {
    this.data = data;
  }

  /** Reads `width` bits (width <= 12 for our purposes) and returns them as an unsigned integer. */
  readBits(width: number): number {
    let value = 0;
    for (let i = 0; i < width; i++) {
      const bytePos = this.bitPos >> 3;
      if (bytePos >= this.data.length) {
        throw new Error(`compression(LZW): ran out of input while reading a ${width}-bit code`);
      }
      const bit = (this.data[bytePos]! >> (this.bitPos & 7)) & 1;
      value |= bit << i;
      this.bitPos++;
    }
    return value;
  }
}

export function decodeLzw(payload: Uint8Array, uncompressedSize: number): Uint8Array {
  const out = new Uint8Array(uncompressedSize);
  let outPos = 0;
  const bits = new LsbBitReader(payload);

  // table[code] holds the byte string that code decodes to, for codes >= 0x101.
  let table: Uint8Array[] = [];
  let nextCode = LZW_FIRST_CODE;
  let width = LZW_MIN_WIDTH;
  let previous: Uint8Array | null = null;
  let codewordsSinceReset = 0;

  const resetDictionary = (): void => {
    table = [];
    nextCode = LZW_FIRST_CODE;
    width = LZW_MIN_WIDTH;
    previous = null;
    codewordsSinceReset = 0;
  };

  while (outPos < uncompressedSize) {
    const code = bits.readBits(width);
    codewordsSinceReset++;

    if (code === LZW_RESET_CODE) {
      while (codewordsSinceReset % 8 !== 0) {
        bits.readBits(width);
        codewordsSinceReset++;
      }
      resetDictionary();
      continue;
    }

    let entry: Uint8Array;
    if (code < 256) {
      entry = Uint8Array.of(code);
    } else if (code < nextCode) {
      const existing = table[code];
      if (!existing) {
        throw new Error(`compression(LZW): code ${code} has no dictionary entry at output byte ${outPos}`);
      }
      entry = existing;
    } else if (code === nextCode && previous !== null) {
      // The classic LZW "KwKwK" case: the encoder just emitted the code it
      // is about to define, whose expansion is previous + previous[0].
      entry = new Uint8Array(previous.length + 1);
      entry.set(previous);
      entry[previous.length] = previous[0]!;
    } else {
      throw new Error(`compression(LZW): code ${code} is invalid (next free code is ${nextCode})`);
    }

    if (outPos + entry.length > uncompressedSize) {
      throw new Error(
        `compression(LZW): decoded entry overruns declared size (${outPos + entry.length} > ${uncompressedSize})`,
      );
    }
    out.set(entry, outPos);
    outPos += entry.length;

    if (previous !== null && nextCode < LZW_MAX_TABLE_SIZE) {
      const newEntry = new Uint8Array(previous.length + 1);
      newEntry.set(previous);
      newEntry[previous.length] = entry[0]!;
      table[nextCode] = newEntry;
      nextCode++;
    }
    if (nextCode >= 1 << width && width < LZW_MAX_WIDTH) {
      width++;
    }
    previous = entry;
  }

  return out;
}
